import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { transactionService } from '@/services/transactionService';

// Define types locally since they might not exist in @/types/api
interface Transaction {
  id: string;
  amount: number;
  status: string;
  created_at: string;
  updated_at: string;
  // Add other transaction properties as needed
}

interface TransactionFilters {
  page: number;
  limit: number;
  status: string;
  startDate: string;
  endDate: string;
  search: string;
}

interface TransactionListResponse {
  data: Transaction[];
  current_page: number;
  last_page: number;
  total: number;
  per_page: number;
}

// Transaction state interface
interface TransactionState {
  transactions: Transaction[];
  currentTransaction: Transaction | null;
  loading: boolean;
  error: string | null;
  filters: TransactionFilters;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
}

// Initial state
const initialState: TransactionState = {
  transactions: [],
  currentTransaction: null,
  loading: false,
  error: null,
  filters: {
    page: 1,
    limit: 10,
    status: '',
    startDate: '',
    endDate: '',
    search: '',
  },
  pagination: {
    currentPage: 1,
    totalPages: 0,
    totalItems: 0,
    itemsPerPage: 10,
  },
};

// Async thunks
export const fetchTransactions = createAsyncThunk(
  'transactions/fetchTransactions',
  async (filters: TransactionFilters, { rejectWithValue }) => {
    try {
      const response = await transactionService.getTransactions(filters.page, filters.limit);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch transactions');
    }
  }
);

export const fetchTransactionById = createAsyncThunk(
  'transactions/fetchTransactionById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await transactionService.getTransactions(1, 1); // Mock implementation
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch transaction');
    }
  }
);

export const updateTransactionStatus = createAsyncThunk(
  'transactions/updateStatus',
  async ({ id, status }: { id: string; status: string }, { rejectWithValue }) => {
    try {
      // Mock implementation - replace with actual API call
      const response = { id, status };
      return { id, status, response };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to update transaction status');
    }
  }
);

// Transaction slice
const transactionsSlice = createSlice({
  name: 'transactions',
  initialState,
  reducers: {
    setFilters: (state, action: PayloadAction<Partial<TransactionFilters>>) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        page: 1,
        limit: 10,
        status: '',
        startDate: '',
        endDate: '',
        search: '',
      };
    },
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentTransaction: (state) => {
      state.currentTransaction = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Transactions
      .addCase(fetchTransactions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransactions.fulfilled, (state, action: PayloadAction<TransactionListResponse>) => {
        state.loading = false;
        state.transactions = action.payload.data;
        state.pagination = {
          currentPage: action.payload.current_page,
          totalPages: action.payload.last_page,
          totalItems: action.payload.total,
          itemsPerPage: action.payload.per_page,
        };
        state.error = null;
      })
      .addCase(fetchTransactions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Fetch Transaction by ID
      .addCase(fetchTransactionById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTransactionById.fulfilled, (state, action: PayloadAction<Transaction>) => {
        state.loading = false;
        state.currentTransaction = action.payload;
        state.error = null;
      })
      .addCase(fetchTransactionById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      
      // Update Transaction Status
      .addCase(updateTransactionStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTransactionStatus.fulfilled, (state, action) => {
        state.loading = false;
        const { id, status } = action.payload;
        const transaction = state.transactions.find(t => t.id === id);
        if (transaction) {
          transaction.status = status;
        }
        if (state.currentTransaction?.id === id) {
          state.currentTransaction.status = status;
        }
        state.error = null;
      })
      .addCase(updateTransactionStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setFilters, clearFilters, clearError, clearCurrentTransaction } = transactionsSlice.actions;
export default transactionsSlice;
